import episodeOfCareService from 'src/services/apiServices/episodeOfCare/episodeOfCare';

export interface GetFormSubmissionUuidParams {
  episodeOfCareId?: any;
  formId?: any;
  uuid?: any;
  activeTab?: any;
  providerId?: any;
  reduxScheduledDocuments?: any[] | null;
}

export interface ScheduledDocument {
  uuid: string;
  reformId?: string;
  formId?: string;
  formType: string;
  dueDate: string;
  [key: string]: any;
}

export interface GetFormSubmissionUuidResponse {
  formSubmissionUuid: string | false;
}

/**
 * Fetches scheduled documents and finds the correct form submission UUID
 * for a given form by matching against the scheduled documents API.
 * Handles all business logic, logging, and fallback scenarios.
 *
 * Strategy:
 * 1. First tries to use Redux state (if reduxScheduledDocuments provided)
 * 2. Falls back to API call if Redux data unavailable or no match found
 *
 * Note: The API response data is nested as response.data.data.content
 * (not the more common response.data.content structure)
 *
 * @param params - Parameters needed to fetch and match the document
 * @param params.reduxScheduledDocuments - Optional Redux state data to try first
 * @returns Promise containing the form submission UUID or fallback value
 */
export const getFormSubmissionUuid = async (
  params: GetFormSubmissionUuidParams
): Promise<GetFormSubmissionUuidResponse> => {
  const { episodeOfCareId, formId, uuid, activeTab, providerId, reduxScheduledDocuments } = params;

  // Convert parameters to strings and validate
  const episodeOfCareIdStr = episodeOfCareId ? String(episodeOfCareId) : '';
  const formIdStr = formId ? String(formId) : '';
  const uuidStr = uuid ? String(uuid) : '';
  const activeTabStr = activeTab ? String(activeTab) : '';
  const providerIdStr = providerId ? String(providerId) : '';

  // Try to use Redux state first if available
  if (reduxScheduledDocuments && Array.isArray(reduxScheduledDocuments) && formIdStr) {
    const matchedDocument = findMatchingDocument(reduxScheduledDocuments, formIdStr, uuidStr);

    if (matchedDocument?.uuid) {
      return { formSubmissionUuid: matchedDocument.uuid };
    }
  }

  // Check if we have required parameters for API call
  if (!episodeOfCareIdStr || !providerIdStr || !activeTabStr || !formIdStr) {
    return { formSubmissionUuid: false };
  }

  try {
    const response = await episodeOfCareService.getEpisodeOfCareRequiredDocument({
      therapistId: providerIdStr,
      params: {
        episodeOfCareId: episodeOfCareIdStr,
        type: activeTabStr,
        page: 0,
        pageSize: 100,
        sortBy: 'dueDate',
        sortDirection: 'asc',
      }
    });

    // Note: The data is nested as response.data.data.content (not response.data.content)
    if (response?.status !== 200 || !response?.data?.data?.content || !Array.isArray(response.data.data.content)) {
      return { formSubmissionUuid: false };
    }

    const scheduledDocuments = response.data.data.content;

    // Find the matching document using multiple strategies
    const matchedDocument = findMatchingDocument(scheduledDocuments, formIdStr, uuidStr);

    if (matchedDocument?.uuid) {
      return { formSubmissionUuid: matchedDocument.uuid };
    } else {
      return { formSubmissionUuid: false };
    }
  } catch (error) {
    return { formSubmissionUuid: false };
  }
};

/**
 * Attempts to find a matching document using multiple strategies
 *
 * @param documents - Array of scheduled documents
 * @param formId - The form ID to match against
 * @param uuid - The current UUID to use as fallback
 * @returns The matching document or null if not found
 */
function findMatchingDocument(
  documents: ScheduledDocument[],
  formId: string,
  uuid: string
): ScheduledDocument | null {
  // Strategy 1: Try matching by reformId (most common case)
  let matchedDocument = documents.find(
    (doc: ScheduledDocument) => doc?.reformId === formId
  );

  // Strategy 2: If no match by reformId, try matching by formId field
  if (!matchedDocument) {
    matchedDocument = documents.find(
      (doc: ScheduledDocument) => doc?.formId === formId
    );
  }

  // Strategy 3: If still no match, try matching by current uuid as fallback
  if (!matchedDocument && uuid) {
    matchedDocument = documents.find(
      (doc: ScheduledDocument) => doc?.uuid === uuid
    );
  }

  return matchedDocument || null;
}
